import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import Logo from '../../components/common/Logo';
import { offerDetails } from '../../services/api';

interface OfferData {
  id: number;
  title: string;
  image: string;
  description: string;
  price: string;
  trackingId: string | null;
  seller: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
}

const OfferPage: React.FC = () => {
  const { offerCode } = useParams<{ offerCode: string }>();
  const navigate = useNavigate();
  const [offerData, setOfferData] = useState<OfferData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOfferDetails = async () => {
      if (!offerCode) {
        setError('No offer code provided');
        setLoading(false);
        return;
      }

      // Check if offerCode is a base64 encoded email|trackingID
      try {
        const decoded = atob(offerCode);
        if (decoded.includes('|')) {
          const parts = decoded.split('|');
          if (parts.length === 2 && parts[0].includes('@')) {
            // It's a base64 encoded string of email|trackingID, redirect to tracking page to complete captcha
            navigate(`/offer/${offerCode}/tracking`);
            return;
          }
        }
      } catch (e) {
        // Not a valid base64 string, proceed normally
      }

      try {
        const response = await offerDetails.getOfferByIdForGuestUser(offerCode);

        if (response.success === false) {
          setError(response.message || 'Failed to load offer details');
          setLoading(false);
        } else {
          setOfferData({
            id: response.data.id,
            title: response.data.title,
            image: response.data.image,
            description: response.data.description,
            price: response.data.price,
            trackingId: response.data.trackingId,
            seller: response.data.seller,
          });
          setLoading(false);
        }
      } catch (err: any) {
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load offer details';
        setError(errorMessage);
        setLoading(false);
      }
    };

    fetchOfferDetails();
  }, [offerCode]);

  const handleContinue = () => {
    if (offerData?.trackingId) {
      navigate(`/offer/${offerCode}/tracking`, { state: { offerData } });
    } else {
      navigate(`/offer/${offerCode}/buyer-details`, { state: { offerData } });
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      {/* Logo at the top */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Logo variant="full" size="small" />
      </Box>

      <Paper
        elevation={ 0 }
        sx={{
          p: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          Offer Details
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : offerData ? (
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {offerData.title}
            </Typography>

            {offerData.image && (
              <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={offerData.image}
                  alt={offerData.title}
                  sx={{ objectFit: 'contain' }}
                />
              </Card>
            )}

            <CardContent sx={{ p: 0, mb: 3 }}>
              <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
                ${offerData.price}
              </Typography>

              {offerData.description && (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
                  {offerData.description}
                </Typography>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Seller: {offerData.seller.firstName + " " + offerData.seller.lastName}
                </Typography>
                {offerData.seller.email && (
                  <Typography variant="body2" color="text.secondary">
                    {offerData.seller.email}
                  </Typography>
                )}
              </Box>
            </CardContent>

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleContinue}
              sx={{
                borderRadius: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 'bold',
              }}
            >
              Continue
            </Button>
          </Box>
        ) : null}
      </Paper>
    </Container>
  );
};

export default OfferPage;
